/**
 * FILE-OVERVIEW:
 * - 役割: スケジュールREST APIとAdminQueries呼び出しの中核。
 * - 主な連携: hooks/useCalendarEvents.js, utils/eventHandlers.js, components/Calendar/EventDialog.js
 * - 本番運用上の注意: 認証ヘッダー形式・エラーハンドリングを変えると全機能へ波及する。
 */

import { fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import awsExports from '../aws-exports';
import { logError } from './appLogger';
import { shouldExcludeAssignableUser } from '../utils/assignableUser';

/**
 * スケジュール1件。DynamoDB に保存される形そのもの。
 * APIは項目を欠いたまま返すことがあるため、ID以外は任意にしている
 */
export interface CalendarEvent {
  /** 複製時は null を入れて「新規扱い」にするため、null も取り得る */
  id?: string | null;
  /** 旧データに存在するID。出稿時のフォールバックとして参照する */
  _id?: string;
  title?: string;
  /** 開始日時。"YYYY-MM-DDTHH:mm" 形式 */
  start?: string;
  /** 終了日時。同上 */
  end?: string;
  /** start と同じ値。既存データとの互換のため残している */
  date?: string;
  notes?: string;
  person?: string;
  category?: string;
  area?: string;
  allDay?: boolean;
  /** 作成者。未ログイン時は CurrentUser.userId が null になるため null も取り得る */
  createdBy?: string | null;
  createdByUsername?: string | null;
  updatedBy?: string | null;
  updatedByUsername?: string | null;
  createdAt?: string;
  /** プライベート予定かの判定に使う旧フィールド。文字列 'true' が入ることがある */
  isPrivate?: boolean | string;
  /** 同上 */
  private?: boolean | string;
  /** ダイアログのフォーム用。APIへ送る前に取り除く */
  startTime?: string;
  /** 同上 */
  endTime?: string;
}

/** ログイン中の利用者。取得に失敗した場合は全項目が空で isAuthenticated が false になる */
export interface CurrentUser {
  userId: string | null;
  username: string | null;
  name: string | null;
  email: string | null;
  groups: string[];
  isDesk: boolean;
  isReporter: boolean;
  isReadOnly: boolean;
  isAuthenticated: boolean;
}

/** 担当者として割り当て可能なユーザー */
export interface AssignableUser {
  userId: string;
  username: string;
  name: string;
  email: string;
  groups: string[];
  /** グループラベルを前置した表示名。例:【本社・社会】山田 */
  displayName: string;
}

/** リクエストヘッダー */
type RequestHeaders = Record<string, string>;

const API_URL = process.env.REACT_APP_API_URL;
const ADMIN_QUERIES_ENDPOINT = (awsExports.aws_cloud_logic_custom || []).find(
  (entry) => entry?.name === 'AdminQueries'
)?.endpoint;

const assignableUsersCache: {
  value: AssignableUser[] | null;
  expiresAt: number;
  pending: Promise<AssignableUser[]> | null;
} = {
  value: null,
  expiresAt: 0,
  pending: null,
};

const USER_LIST_CACHE_TTL = 30000;
const ASSIGNABLE_USER_GROUP_LABELS: Record<string, string> = {
  'group_society': '【本社・社会】',
  'group_politics-economics': '【本社・政経】',
  'group_sports': '【本社・運動】',
  'group_photo': '【本社・写真】',
  'group_local': '【本社・地域】',
  'group_culture': '【本社・文化】',
  'group_digital': '【本社・デジタル】',
  'group_editorial': '【本社・論説】',
  'group_publication': '【本社・出版】',
  'branch_aomori': '【青森】',
  'branch_tokyo': '【東京】',
  'branch_towada': '【十和田】',
  'branch_misawa': '【三沢】',
  'branch_mutsu': '【むつ】',
  'branch_sannohe': '【三戸】',
  'branch_gonohe': '【五戸】',
  'branch_morioka': '【盛岡】',
  'branch_kuji': '【久慈】',
  'branch_ninohe': '【二戸】',
  'system': '【システム】',
  'Desks': '【デスク】',
};

const ASSIGNABLE_USER_GROUP_PRIORITY = Object.keys(ASSIGNABLE_USER_GROUP_LABELS);

// 環境変数チェック
if (!API_URL) {
  throw new Error('REACT_APP_API_URL が設定されていません。環境変数を確認してください。');
}

/**
 * 認証トークンを取得する関数
 */
const getAuthToken = async (): Promise<string> => {
  try {
    const session = await fetchAuthSession();
    
    // IDトークンを優先使用（Lambda認証で推奨）
    const idToken = session.tokens?.idToken?.toString();
    if (idToken) {
      return idToken;
    }
    
    // フォールバックでアクセストークン使用
    const accessToken = session.tokens?.accessToken?.toString();
    if (accessToken) {
      return accessToken;
    }
    
    throw new Error('有効な認証トークンが取得できません');
  } catch (error) {
    console.error('認証トークン取得エラー:', error);
    throw error;
  }
};

/**
 * AdminQueries向け認証トークンを取得する関数
 * API Gateway(Cognito Authorizer)では access token の方が確実に通るため優先する
 */
const getAdminQueriesToken = async (): Promise<string> => {
  try {
    const session = await fetchAuthSession();

    const accessToken = session.tokens?.accessToken?.toString();
    if (accessToken) {
      return accessToken;
    }

    const idToken = session.tokens?.idToken?.toString();
    if (idToken) {
      return idToken;
    }

    throw new Error('有効なAdminQueries認証トークンが取得できません');
  } catch (error) {
    console.error('AdminQueriesトークン取得エラー:', error);
    throw error;
  }
};

/**
 * 現在のユーザー情報を取得する関数
 *
 * getCurrentUser は呼ばずに idToken の payload からユーザー識別子を取得する。
 * fetchAuthSession と fetchUserAttributes を Promise.all で並列化することで
 * 初期表示時の認証往復を短縮する。
 */
export const getCurrentUserInfo = async (): Promise<CurrentUser> => {
  try {
    const [session, attributes] = await Promise.all([
      fetchAuthSession(),
      fetchUserAttributes().catch(() => ({}) as Awaited<ReturnType<typeof fetchUserAttributes>>),
    ]);

    const idToken = session.tokens?.idToken;
    // IDトークンのクレームは型上 JsonValue のため、参照する項目だけ形を指定する
    const claims = (idToken?.payload || {}) as {
      sub?: string;
      email?: string;
      'cognito:username'?: string;
      'cognito:groups'?: string[];
    };
    const groups = claims['cognito:groups'] || [];

    return {
      userId: claims.sub ?? null,
      username: claims['cognito:username'] || claims.email || null,
      name: attributes.name || claims['cognito:username'] || claims.email || null,
      email: claims.email ?? null,
      groups,
      isDesk: groups.includes('Desks'),
      isReporter: groups.includes('Reporters'),
      isReadOnly: groups.some((g) => String(g || '').toLowerCase() === 'read_only'),
      isAuthenticated: true
    };
  } catch (error) {
    console.error('ユーザー情報取得エラー:', error);
    return {
      userId: null,
      username: null,
      name: null,
      email: null,
      groups: [],
      isDesk: false,
      isReporter: false,
      isReadOnly: false,
      isAuthenticated: false
    };
  }
};

/**
 * リクエストヘッダーを生成する関数
 */
const getHeaders = async (): Promise<RequestHeaders> => {
  const headers: RequestHeaders = {
    'Content-Type': 'application/json'
  };
  
  try {
    const token = await getAuthToken();
    headers['Authorization'] = `Bearer ${token}`;
  } catch (error) {
    // 認証トークン取得に失敗した場合は匿名アクセス
    console.warn('認証トークン取得失敗 - 匿名アクセス');
  }
  
  return headers;
};

const getAdminQueriesHeaders = async (): Promise<RequestHeaders> => {
  const headers: RequestHeaders = {
    'Content-Type': 'application/json'
  };

  try {
    const token = await getAdminQueriesToken();
    headers['Authorization'] = `Bearer ${token}`;
  } catch (error) {
    console.warn('AdminQueriesトークン取得失敗');
  }

  return headers;
};

/**
 * APIエラーハンドリング関数
 */
const handleApiError = async (res: Response, operation: string): Promise<never> => {
  let errorMessage = `${operation}に失敗しました`;
  
  try {
    const errorData = await res.json();
    if (errorData.message) {
      errorMessage = errorData.message;
    }
  } catch (e) {
    // JSONパースに失敗した場合はデフォルトメッセージを使用
  }

  await logError('api_request_failed', {
    operation,
    status: res.status,
    message: errorMessage,
  });

  switch (res.status) {
    case 401:
      throw new Error('認証エラー: ログインしてください');
    case 403:
      throw new Error(`権限エラー: ${errorMessage}`);
    case 404:
      throw new Error('データが見つかりません');
    case 400:
      throw new Error('リクエストエラー: 入力内容を確認してください');
    case 500:
      throw new Error('サーバーエラーが発生しました');
    default:
      throw new Error(errorMessage);
  }
};

// Cognitoの属性は [{Name, Value}] 形式と {key: value} 形式のどちらでも返り得る
const getCognitoAttributeValue = (attributes: unknown, key: string): string => {
  if (Array.isArray(attributes)) {
    const found = (attributes as Array<{ Name?: string; name?: string; Value?: string; value?: string }>)
      .find((attr) => (attr?.Name || attr?.name) === key);
    return found?.Value || found?.value || '';
  }
  if (attributes && typeof attributes === 'object') {
    return (attributes as Record<string, string | undefined>)[key] || '';
  }
  return '';
};

const getAssignableUserGroupLabel = (groups: unknown): string => {
  const normalizedGroups = Array.from(new Set((Array.isArray(groups) ? groups : [])
    .map((group) => String(group || '').trim())
    .filter(Boolean)));
  const labels = ASSIGNABLE_USER_GROUP_PRIORITY
    .filter((group) => normalizedGroups.includes(group))
    .map((group) => ASSIGNABLE_USER_GROUP_LABELS[group])
    .filter(Boolean);
  return labels.join('');
};

const getAssignableUserGroupSortIndex = (groups: unknown): number => {
  const normalizedGroups = Array.from(new Set((Array.isArray(groups) ? groups : [])
    .map((group) => String(group || '').trim())
    .filter(Boolean)));
  const index = ASSIGNABLE_USER_GROUP_PRIORITY.findIndex((group) => normalizedGroups.includes(group));
  return index >= 0 ? index : ASSIGNABLE_USER_GROUP_PRIORITY.length;
};

const getAssignableUserGroupMatchCount = (groups: unknown): number => {
  const normalizedGroups = Array.from(new Set((Array.isArray(groups) ? groups : [])
    .map((group) => String(group || '').trim())
    .filter(Boolean)));
  return ASSIGNABLE_USER_GROUP_PRIORITY.filter((group) => normalizedGroups.includes(group)).length;
};

const getAssignableUserDisplayName = (user: { name?: string; username?: string; groups?: unknown }): string => {
  const baseName = String(user?.name || user?.username || '').trim();
  if (!baseName) return '';
  const groupLabel = getAssignableUserGroupLabel(user?.groups);
  return groupLabel ? `${groupLabel}${baseName}` : baseName;
};

const getUserNameByUsername = async (username: string, headers: RequestHeaders): Promise<string> => {
  if (!username) return '';
  try {
    const url = `${ADMIN_QUERIES_ENDPOINT}/getUser?username=${encodeURIComponent(username)}&_t=${Date.now()}`;
    const res = await fetch(url, {
      method: 'GET',
      headers,
      cache: 'no-store',
    });
    if (!res.ok) return '';
    const payload = await res.json();
    return getCognitoAttributeValue(payload?.UserAttributes || payload?.Attributes, 'name');
  } catch (error) {
    console.warn('ユーザー詳細取得に失敗:', username, error);
    return '';
  }
};

const getUserGroupsByUsername = async (username: string, headers?: RequestHeaders): Promise<string[]> => {
  if (!username || !ADMIN_QUERIES_ENDPOINT) return [];

  try {
    const effectiveHeaders = headers || await getAdminQueriesHeaders();
    let nextToken: string | null = null;
    const allGroups: Array<{ GroupName?: string; groupName?: string }> = [];

    do {
      const params = new URLSearchParams({
        username,
        limit: '60',
        _t: String(Date.now()),
        ...(nextToken ? { token: nextToken } : {}),
      });
      const url = `${ADMIN_QUERIES_ENDPOINT}/listGroupsForUser?${params.toString()}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: effectiveHeaders,
        cache: 'no-store',
      });

      if (!res.ok) return [];

      // payload に型を付けないと nextToken の推論がループ内で循環する
      const payload = await res.json() as {
        Groups?: Array<{ GroupName?: string; groupName?: string }>;
        groups?: Array<{ GroupName?: string; groupName?: string }>;
        NextToken?: string;
        nextToken?: string;
      };
      const groups = (Array.isArray(payload?.Groups) && payload.Groups)
        || (Array.isArray(payload?.groups) && payload.groups)
        || [];
      allGroups.push(...groups);
      nextToken = payload?.NextToken || payload?.nextToken || null;
    } while (nextToken);

    return allGroups
      .map((group) => String(group?.GroupName || group?.groupName || '').trim())
      .filter(Boolean);
  } catch (error) {
    console.warn('ユーザーグループ取得に失敗:', username, error);
    return [];
  }
};

export { getUserGroupsByUsername };

/**
 * 複数 username から Cognito の name 属性を解決して辞書化する。
 *
 * @param {string[]} usernames Cognito username 配列
 * @returns {Promise<Record<string, string>>} { username: name } 形式のマップ
 */
export const resolveUserNamesByUsernames = async (usernames: string[]): Promise<Record<string, string>> => {
  if (!ADMIN_QUERIES_ENDPOINT) return {};

  const values = Array.from(new Set((Array.isArray(usernames) ? usernames : [])
    .map((v) => String(v || '').trim())
    .filter(Boolean)));

  if (values.length === 0) return {};

  try {
    const headers = await getAdminQueriesHeaders();
    const pairs = await Promise.all(
      values.map(async (username) => {
        const name = await getUserNameByUsername(username, headers);
        return [username, name || ''];
      })
    );
    return Object.fromEntries(pairs);
  } catch (error) {
    console.error('ユーザー名解決エラー:', error);
    return {};
  }
};

/**
 * 割り当て可能ユーザー一覧を Cognito から取得して整形する。
 *
 * 処理内容:
 * - AdminQueries/listUsers をページング取得
 * - name 補完、groups 取得、displayName 付与
 * - グループ優先順位で並べ替え
 * - 短期キャッシュ（USER_LIST_CACHE_TTL）
 *
 * @returns {Promise<Array<{userId:string,username:string,name:string,email:string,groups:string[],displayName:string}>>}
 */
export const listAssignableUsers = async (): Promise<AssignableUser[]> => {
  if (!ADMIN_QUERIES_ENDPOINT) {
    console.warn('AdminQueries endpoint が未設定のためユーザー一覧を取得できません');
    return [];
  }

  const now = Date.now();
  if (assignableUsersCache.value && assignableUsersCache.expiresAt > now) {
    return assignableUsersCache.value;
  }
  if (assignableUsersCache.pending) {
    return assignableUsersCache.pending;
  }

  const pending = (async () => {
    const headers = await getAdminQueriesHeaders();
    const allUsers: Array<{
      Username?: string;
      username?: string;
      Attributes?: unknown;
      attributes?: unknown;
      UserAttributes?: unknown;
    }> = [];
    let nextToken: string | null = null;
    const pageLimit = 60;

    do {
      const params = new URLSearchParams({
        limit: String(pageLimit),
        _t: String(Date.now()),
        ...(nextToken ? { token: nextToken } : {}),
      });
      const url = `${ADMIN_QUERIES_ENDPOINT}/listUsers?${params.toString()}`;
      const res = await fetch(url, {
        method: 'GET',
        headers,
        cache: 'no-store',
      });

      if (!res.ok) {
        await handleApiError(res, 'ユーザー一覧取得');
      }

      // payload に型を付けないと nextToken の推論がループ内で循環する
      const payload = await res.json() as {
        Users?: typeof allUsers;
        users?: typeof allUsers;
        NextToken?: string;
        nextToken?: string;
      };
      const page =
        (Array.isArray(payload?.Users) && payload.Users) ||
        (Array.isArray(payload?.users) && payload.users) ||
        [];
      allUsers.push(...page);
      nextToken = payload?.NextToken || payload?.nextToken || null;
    } while (nextToken);

    const mapped = allUsers.map((user) => {
      const attrs = user?.Attributes || user?.attributes || user?.UserAttributes || [];
      const sub = getCognitoAttributeValue(attrs, 'sub');
      const email = getCognitoAttributeValue(attrs, 'email');
      const name = getCognitoAttributeValue(attrs, 'name');
      const preferredUsername = getCognitoAttributeValue(attrs, 'preferred_username');
      const username = user?.Username || user?.username || preferredUsername || email || '';
      return {
        userId: sub,
        username,
        name: name || '',
        email,
      };
    }).filter((u) => u.userId && !shouldExcludeAssignableUser(u));

    const completed = await Promise.all(
      mapped.map(async (u) => {
        if (u.name) return u;
        const fetchedName = await getUserNameByUsername(u.username, headers);
        return {
          ...u,
          name: fetchedName || u.name || '',
        };
      })
    );

    const withGroups = await Promise.all(
      completed.map(async (u) => ({
        ...u,
        groups: await getUserGroupsByUsername(u.username, headers),
      }))
    );

    const withDisplayNames = withGroups.map((user) => ({
      ...user,
      displayName: getAssignableUserDisplayName(user),
    }));

    const sorted = withDisplayNames.sort((a, b) => {
      const aGroupIndex = getAssignableUserGroupSortIndex(a?.groups);
      const bGroupIndex = getAssignableUserGroupSortIndex(b?.groups);
      if (aGroupIndex !== bGroupIndex) return aGroupIndex - bGroupIndex;

      const aGroupMatchCount = getAssignableUserGroupMatchCount(a?.groups);
      const bGroupMatchCount = getAssignableUserGroupMatchCount(b?.groups);
      if (aGroupMatchCount !== bGroupMatchCount) return bGroupMatchCount - aGroupMatchCount;

      const nameCompare = String(a?.name || a?.username || '').localeCompare(String(b?.name || b?.username || ''), 'ja');
      if (nameCompare !== 0) return nameCompare;

      return String(a?.username || '').localeCompare(String(b?.username || ''), 'ja');
    });
    assignableUsersCache.value = sorted;
    assignableUsersCache.expiresAt = Date.now() + USER_LIST_CACHE_TTL;
    return sorted;
  })();
  assignableUsersCache.pending = pending;

  try {
    return await pending;
  } catch (error) {
    console.error('ユーザー一覧取得エラー:', error);
    return [];
  } finally {
    assignableUsersCache.pending = null;
  }
};

/**
 * スケジュール一覧を取得する。
 *
 * @returns {Promise<Array>} スケジュール配列（失敗時は空配列）
 */
export const loadCalendarEvents = async (): Promise<CalendarEvent[]> => {
  try {
    const headers = await getHeaders();
    
    const res = await fetch(API_URL, { 
      method: 'GET',
      headers
    });
    
    if (!res.ok) {
      await handleApiError(res, 'イベント取得');
    }
    
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('イベント取得エラー:', error);
    return [];
  }
};

/**
 * スケジュールを新規作成する。
 *
 * @param {Object} event 作成対象イベント
 * @returns {Promise<Object>} 作成後イベント
 */
export const createCalendarEvent = async (event: CalendarEvent): Promise<CalendarEvent> => {
  try {
    const headers = await getHeaders();
    
    const res = await fetch(API_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify(event),
    });
    
    if (!res.ok) {
      await handleApiError(res, 'イベント新規作成');
    }
    
    return await res.json();
  } catch (error) {
    console.error('イベント新規作成エラー:', error);
    throw error;
  }
};

/**
 * スケジュールを全体更新（PUT）する。
 *
 * @param {string} id イベントID
 * @param {Object} event 更新イベント全文
 * @returns {Promise<Object>} 更新後イベント
 */
export const updateCalendarEvent = async (id: string, event: CalendarEvent): Promise<CalendarEvent> => {
  try {
    const headers = await getHeaders();
    
    const res = await fetch(`${API_URL}/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(event),
    });
    
    if (!res.ok) {
      await handleApiError(res, 'イベント更新');
    }
    
    return await res.json();
  } catch (error) {
    console.error('イベント更新エラー:', error);
    throw error;
  }
};

/**
 * スケジュールを部分更新（PATCH）する。
 *
 * @param {string} id イベントID
 * @param {Object} updates 差分フィールド
 * @returns {Promise<Object>} 更新後イベント
 */
export const patchCalendarEvent = async (id: string, updates: Partial<CalendarEvent>): Promise<CalendarEvent> => {
  try {
    const headers = await getHeaders();
    
    const res = await fetch(`${API_URL}/${id}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(updates),
    });
    
    if (!res.ok) {
      await handleApiError(res, 'イベント部分更新');
    }
    
    return await res.json();
  } catch (error) {
    console.error('イベント部分更新エラー:', error);
    throw error;
  }
};

/**
 * スケジュールを削除する。
 *
 * @param {string} id イベントID
 * @returns {Promise<Object>} APIレスポンス
 */
export const deleteCalendarEvent = async (id: string): Promise<unknown> => {
  try {
    const headers = await getHeaders();
    
    const res = await fetch(`${API_URL}/${id}`, {
      method: 'DELETE',
      headers,
    });
    
    if (!res.ok) {
      await handleApiError(res, 'イベント削除');
    }
    
    return await res.json();
  } catch (error) {
    console.error('イベント削除エラー:', error);
    throw error;
  }
};

/**
 * API疎通確認（開発環境専用）。
 *
 * @returns {Promise<Object|null>} 本番環境では null を返す
 */
export const testConnection = async (): Promise<unknown> => {
  if (process.env.NODE_ENV === 'production') {
    console.warn('本番環境では接続テストは実行されません');
    return null;
  }
  
  try {
    const headers = await getHeaders();
    
    const response = await fetch(API_URL, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    
    const data = await response.json();
    console.log('接続テスト成功');
    return data;
  } catch (error) {
    console.error('接続テストエラー:', error);
    throw error;
  }
};